import { useRealtimeAi } from "@/hooks/use-realtime-ai";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Bot,
  MessageSquare,
  BookMarked,
  CalendarDays,
  History,
  Workflow,
  Settings,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { AppLayout } from "@/components/layout/app-layout";
import { AIChat, AIConversationList } from "@/components/ai/ai-chat";
import { AIKnowledgeDialog } from "@/components/ai/ai-knowledge";
import { AiActivity } from "@/components/ai/ai-history";
import { AssistantPreferences } from "@/components/ai/assistant-preferences";
import { AssistantAutomations } from "@/components/ai/assistant-automations";
import { useAuthStore } from "@/stores/auth-store";
import { useI18n } from "@/lib/i18n";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";

export function AssistantPage() {
  useRealtimeAi("");
  const user = useAuthStore((state) => state.user)!;
  const fr = useI18n().language === "fr";
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [area, setArea] = useState(params.get("area") ?? "conversations");
  useEffect(() => {
    if (area === "settings" && params.get("focus") === "budget") {
      document
        .getElementById("automation-token-budget")
        ?.scrollIntoView({ block: "center" });
      document.getElementById("monthly-token-budget")?.focus();
    }
  }, [area, params]);
  const [active, setActive] = useState<string | null>(
    params.get("conversation") ??
      localStorage.getItem(`taskflow:assistant:chat:${user.id}`),
  );
  const [initialPrompt, setInitialPrompt] = useState(
    params.get("prompt")
      ? { nonce: Date.now(), text: params.get("prompt")! }
      : undefined,
  );
  const chats = useQuery({
    queryKey: ["ai-conversations", ""],
    queryFn: () =>
      apiGet<{ conversations: { id: string; title: string }[] }>(
        "/ai/assistant/conversations",
      ),
  });
  const select = (id: string | null) => {
    setActive(id);
    if (id) localStorage.setItem(`taskflow:assistant:chat:${user.id}`, id);
    else localStorage.removeItem(`taskflow:assistant:chat:${user.id}`);
    setParams(id ? { conversation: id } : {});
  };
  const tabs = [
    {
      id: "conversations",
      label: fr ? "Conversations" : "Conversations",
      icon: MessageSquare,
    },
    { id: "memory", label: fr ? "Mémoire" : "Memory", icon: BookMarked },
    {
      id: "automations",
      label: fr ? "Automatisations" : "Automations",
      icon: Workflow,
    },
    { id: "activity", label: fr ? "Activité" : "Activity", icon: History },
    {
      id: "settings",
      label: fr ? "Préférences" : "Preferences",
      icon: Settings,
    },
  ];
  const openEntity = async (
    id: string,
    kind: "task" | "document",
    proposal?: Record<string, unknown>,
  ) => {
    const entity = await apiGet<{
      id: string;
      projectId: string;
      type?: string;
    }>(`/ai/assistant/resources/${kind === "task" ? "task" : "view"}/${id}`);
    navigate(
      `/projects/${entity.projectId}?${kind === "task" ? "task" : "view"}=${id}`,
      { state: proposal ? { aiDocumentProposal: proposal } : undefined },
    );
  };
  return (
    <AppLayout>
      <div className="flex h-full min-h-0 flex-col gap-4 p-4 sm:p-6">
        <header className="flex items-center gap-3">
          <Bot className="h-6 w-6 text-primary" />
          <div className="flex-1">
            <h1 className="text-2xl font-semibold">Assistant</h1>
            <p className="text-sm text-foreground-muted">
              {fr
                ? "Vos conversations privées, projets et actions, au même endroit."
                : "Your private conversations, projects and actions in one place."}
            </p>
          </div>
          <Link
            to="/dashboard#personal-calendar"
            className="inline-flex items-center gap-2 text-sm text-primary"
          >
            <CalendarDays className="h-4 w-4" />
            <span className="hidden sm:inline">
              {fr ? "Mon calendrier" : "My calendar"}
            </span>
          </Link>
        </header>
        <nav
          aria-label={fr ? "Espaces de l’Assistant" : "Assistant areas"}
          className="flex gap-1 overflow-x-auto rounded-xl border border-white/10 bg-white/[0.03] p-1"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setArea(tab.id);
                setParams({
                  ...(active ? { conversation: active } : {}),
                  area: tab.id,
                });
              }}
              aria-current={area === tab.id ? "page" : undefined}
              className={cn(
                "flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm transition-colors",
                area === tab.id
                  ? "bg-primary/15 text-primary"
                  : "text-foreground-secondary hover:bg-white/5",
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
        {area === "conversations" ? (
          <div className="flex flex-1 min-h-0 gap-4">
            <aside className="hidden md:block w-60 shrink-0 overflow-y-auto rounded-xl border border-white/10 bg-white/[0.03] p-3">
              <AIConversationList
                projectId=""
                activeId={active}
                onSelect={select}
                canCreate
              />
            </aside>
            <div className="flex min-w-0 flex-1 min-h-0 flex-col gap-2">
              <select
                className="md:hidden rounded-lg border border-white/10 bg-surface p-2 text-sm"
                value={active ?? ""}
                aria-label={fr ? "Conversation" : "Conversation"}
                onChange={(event) => select(event.target.value || null)}
              >
                <option value="">
                  {fr ? "Nouvelle conversation" : "New conversation"}
                </option>
                {chats.data?.conversations.map((chat) => (
                  <option key={chat.id} value={chat.id}>
                    {chat.title}
                  </option>
                ))}
              </select>
              <AIChat
                projectId=""
                conversationId={active}
                onSelect={select}
                context={{
                  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                }}
                userId={user.id}
                canCreate
                canManage
                initialPrompt={initialPrompt}
                onConsumePrompt={() => setInitialPrompt(undefined)}
                onTask={(id) => {
                  void openEntity(id, "task");
                }}
                onView={(id) => {
                  void openEntity(id, "document");
                }}
                onDocumentEdit={(id, proposal) => {
                  void openEntity(id, "document", proposal);
                }}
              />
            </div>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-white/10 bg-white/[0.03] p-4 sm:p-6">
            {area === "memory" ? (
              <AIKnowledgeDialog
                embedded
                activeTab="memory"
                projectId=""
                open
                onClose={() => {}}
                canManage={false}
              />
            ) : area === "automations" ? (
              <AssistantAutomations
                onBudget={() => {
                  setArea("settings");
                  setParams({ area: "settings", focus: "budget" });
                }}
              />
            ) : area === "activity" ? (
              <AiActivity />
            ) : (
              <AssistantPreferences />
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
